import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type {
	PricingCampaignModel,
	PricingEngineType,
	ProductsByPCModel,
} from '@/shared/generated'
import type { PricingCampaignDataRow } from '../lib/usePricingCampaignsPickerTable'

import React, { useMemo } from 'react'
import { any, keyBy, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	getOptimizationsMap,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	usePricingCampaignsQuery,
	useProductsCountsByPcQuery,
} from '@/entities/pricing-campaign'

import { MasterTable } from '@/shared/components/master-table'
import SearchField from '@/shared/components/SearchField'
import { Blanket } from '@/shared/components/ui/blanket'
import { Loader } from '@/shared/components/ui/loader'

import { usePricingCampaignsPickerTable } from '../lib/usePricingCampaignsPickerTable'

type Props = {
	excludePricingCampaigns: string[]
	rowSelection: SRT_RowSelectionState
	setRowSelection: (rowSelection: SRT_RowSelectionState) => void
}

export const SelectPricingCampaigns = ({
	rowSelection,
	setRowSelection,
	excludePricingCampaigns,
}: Props) => {
	const { t } = useTranslation()

	const commonPricinCampaignsQuery = usePricingCampaignsQuery<
		PricingCampaignModel[]
	>({})
	const optimizations = useOptimizationsQuery({
		refetchOnMount: 'always',
		select: getOptimizationsMap,
	})
	const productsCountsByPc = useProductsCountsByPcQuery(
		{},
		{
			select: keyBy<ProductsByPCModel>('pricing_campaign_id'),
		}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const isLoading = any(prop('isLoading'), [
		commonPricinCampaignsQuery,
		optimizationGroupsQuery,
	])
	const data: PricingCampaignDataRow[] = useMemo(() => {
		if (optimizationGroupsQuery.data && commonPricinCampaignsQuery.data) {
			const ogMap = keyBy('id', optimizationGroupsQuery.data)

			return (commonPricinCampaignsQuery?.data ?? [])
				.filter(
					(campaign) => excludePricingCampaigns.indexOf(campaign.id) === -1
				)
				.map((campaign): PricingCampaignDataRow => {
					const group = ogMap?.[campaign.optimization_group_id as string]
					const optimization = optimizations?.data?.[group?.id]

					return {
						id: campaign.id,
						name: campaign.name,
						engine: campaign.settings.pricing_tactics
							.engine as PricingEngineType,
						optimization_group_name: group?.name,
						optimization_group_id: group?.id,
						optimization_status: optimization?.status,
						products_count:
							productsCountsByPc.data?.[campaign.id]?.product_count || 0,
					}
				})
		} else {
			return []
		}
	}, [
		optimizationGroupsQuery,
		commonPricinCampaignsQuery,
		optimizations,
		productsCountsByPc,
	])
	const options = useMemo(
		() => ({
			optimization_group_name:
				optimizationGroupsQuery.data?.map((og) => ({
					label: og.name,
					value: og.name,
				})) || [],
		}),
		[optimizationGroupsQuery.data]
	)
	const table = usePricingCampaignsPickerTable({
		data,
		options,
		isLoading,
		rowSelection,
		setRowSelection: setRowSelection as React.Dispatch<
			React.SetStateAction<SRT_RowSelectionState>
		>,
	})
	const nameColumn = React.useMemo(() => table.getColumn('name'), [table])

	return (
		<>
			<div className='flex items-center justify-between'>
				<div className='flex items-center space-x-3'>
					<SearchField
						value={(nameColumn.getFilterValue() as string) ?? ''}
						onValueChange={(text) => nameColumn.setFilterValue(text)}
						placeholder={t('general_search')}
					/>
				</div>
			</div>
			<div className='border-base relative flex h-96 flex-col overflow-hidden rounded-lg border border-solid text-sm'>
				<Blanket
					className='absolute flex items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
					isTinted={isLoading}
				>
					<Loader />
				</Blanket>
				<MasterTable table={table} />
			</div>
		</>
	)
}
